/**
 * PORTED — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/client.tsx`.
 *
 * Portable client (ops already mounted — §3 of shared-render-ownership.md).
 * Co-ported as a private delegate of the `_crm/*View` family so the view
 * rewrites can render. Changes, and only these:
 *   `"use client"`                     -> dropped
 *   `useRouter` / `useSearchParams`    -> `useNavigate` / `useSearch` from the router seam
 *   `next/link`                        -> `RouterLink` (if present)
 *   `router.push/back/refresh`         -> `navigate.*`
 */


/**
 * Client-side glue for the CRM surfaces re-homed inside the `sales` project
 * section. Each component is a thin wrapper that wires `apiCall` callbacks +
 * `router` navigation onto the shared presentational blocks in
 * `@excellent/ui/blocks/crm/*`. Every navigation target is parameterized by
 * `basePath` so the same UI works under `/projects/<slug>/sales/*`.
 *
 * Mirrors the (now-deprecated) product wrappers in `products/crm/app/*`, but
 * basePath-aware so navigation stays in-section.
 */

import { useNavigate, useSearch } from "@excellent/ui/router";
import React, { useMemo, useState } from "react";
import { apiCall } from "@excellent/ui/api";
import {
  leads as leadsContract,
  opportunities as oppsContract,
  calls as callsContract,
  emails as emailsContract,
  sms as smsContract,
  meetings as meetingsContract,
  sequences as sequencesContract,
  templates as templatesContract,
} from "@excellent/ui/api/contracts/crm";
import { LeadTable, type LeadTableRow } from "@excellent/ui/blocks/crm/lead-table";
import {
  SmartViewPicker,
  type SmartView,
} from "@excellent/ui/blocks/crm/smart-view-picker";
import {
  CrmPipelineBoard,
  type PipelineStage,
} from "@excellent/ui/blocks/crm/pipeline-board-adapter";
import type { OpportunityCardData } from "@excellent/ui/blocks/crm/opportunity-card";
import { ActivityComposer } from "@excellent/ui/blocks/crm/activity-composer";
import {
  SequenceEditor,
  type SequenceStep,
} from "@excellent/ui/blocks/crm/sequence-editor";
import { CrmTemplateEditor } from "@excellent/ui/blocks/crm/template-editor";
import {
  Button,
  ComposerSelectPill,
  CreateComposer,
  FormField,
  FormShell,
  Input,
  PageHeader,
  ScopedSearchButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useOpenObject,
} from "@excellent/ui";
import {
  CircleDashed,
  CircleDollarSign,
  GitBranch,
  Milestone,
  Plus,
  Route,
  UserRound,
  Users,
} from "@excellent/ui/icons";

// ------------------------------------------------------------ Leads list

export function LeadsListClient({
  basePath,
  initialLeads,
  statusLabels,
  sourceLabels,
  ownerLabels,
  smartViews,
}: {
  basePath: string;
  initialLeads: LeadTableRow[];
  statusLabels: Record<number, string>;
  sourceLabels: Record<number, string>;
  ownerLabels: Record<number, string>;
  smartViews: SmartView[];
}) {
  const navigate = useNavigate();
  const openObject = useOpenObject();
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [leads, setLeads] = useState<LeadTableRow[]>(initialLeads);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  // The dock owns the query, scoped to this Leads view (Issue 19 /
  // SCOPED-SEARCH-PRD); the page mirrors it from the URL `?q=` and filters its
  // own rows (LeadTable receives the query below).
  const searchParams = useSearch();
  const query = searchParams.get("q") ?? "";
  // Creation stays project-scoped: at the workspace tier (basePath
  // `/projects/sections/<slug>`) `${basePath}/leads/new` has no route, so hide the
  // New button there. Row clicks open the scope-free /o/<uid> at either tier.
  const scoped = !basePath.startsWith("/projects/sections");
  const uidById = useMemo(
    () => new Map(leads.map((l) => [l.id, l.uid])),
    [leads],
  );

  const filteredCount = useMemo(() => {
    if (!query) return leads.length;
    const q = query.toLowerCase();
    return leads.filter((l) => l.display_name.toLowerCase().includes(q)).length;
  }, [leads, query]);

  async function applyView(slug: string | null) {
    setActiveSlug(slug);
    setLoading(true);
    try {
      const r = await apiCall(leadsContract.list, {
        query: slug ? { smart_view_slug: slug } : {},
      });
      setLeads(r.rows as LeadTableRow[]);
    } finally {
      setLoading(false);
    }
  }

  async function archiveSelected() {
    if (selected.length === 0) return;
    await apiCall(leadsContract.bulk_update, {
      body: { lead_ids: selected, patch: { archive: true } },
    });
    setSelected([]);
    await applyView(activeSlug);
  }

  const subtitle =
    query && filteredCount !== leads.length
      ? `${filteredCount} of ${leads.length} leads`
      : `${leads.length} ${leads.length === 1 ? "lead" : "leads"}`;

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Leads"
        description={subtitle}
        toolbar={
          <>
            <ScopedSearchButton label="Search leads" />
            <Button asChild variant="outline">
              <a href="/api/crm/export?entity=leads&format=csv" download>
                Export CSV
              </a>
            </Button>
            {scoped ? (
              <Button
                size="icon-tb"
                aria-label="New lead"
                title="New lead"
                onClick={() => navigate.push(`${basePath}/leads/new`)}
              >
                <Plus className="size-4" />
              </Button>
            ) : null}
          </>
        }
      />
      <SmartViewPicker
        views={smartViews}
        activeSlug={activeSlug}
        onSelect={(s) => applyView(s)}
        onClear={() => applyView(null)}
      />
      {loading && <div className="type-body text-muted-foreground">Loading…</div>}
      <LeadTable
        leads={leads}
        query={query}
        statusLabels={statusLabels}
        sourceLabels={sourceLabels}
        ownerLabels={ownerLabels}
        selected={selected}
        onSelectedChange={setSelected}
        onRowClick={(id) => {
          const uid = uidById.get(id);
          if (uid) openObject(uid);
        }}
        bulkActionLabel="Archive"
        onBulkAction={archiveSelected}
        empty="No leads match this view."
      />
    </div>
  );
}

// ------------------------------------------------------------ New lead

export function NewLeadClient({
  basePath,
  statuses,
  sources,
  embedded = false,
  open,
  onOpenChange,
  onCreated,
  onCancel,
}: {
  basePath: string;
  statuses: { slug: string; name: string }[];
  sources: { slug: string; name: string }[];
  /** Render as the shared Linear-style modal (`CreateComposer`) instead of the
   *  standalone /new page form. `open`/`onOpenChange` drive the dialog. */
  embedded?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Called after a successful create instead of navigating to the new lead
   *  (the modal closes + refreshes the current view). */
  onCreated?: () => void;
  /** Cancel handler; defaults to `navigate.back()` on the dedicated /new page. */
  onCancel?: () => void;
}) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [status, setStatus] = useState("active");
  const [source, setSource] = useState<string>("__none__");
  const [createMore, setCreateMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doSubmit() {
    if (!displayName.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const lead = await apiCall(leadsContract.create, {
        body: {
          display_name: displayName,
          status_slug: status || null,
          source_slug: source === "__none__" ? null : source,
        },
      });
      if (onCreated) {
        if (createMore) {
          setDisplayName("");
          setSource("__none__");
          setBusy(false);
          navigate.refresh();
          return;
        }
        onCreated();
      } else {
        navigate.push(`${basePath}/leads/${lead.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  if (embedded) {
    return (
      <CreateComposer
        open={open ?? false}
        onOpenChange={(o) => onOpenChange?.(o)}
        contextLabel="Lead"
        contextIcon={<Users />}
        heading="New lead"
        title={displayName}
        onTitleChange={setDisplayName}
        titlePlaceholder="Acme Co — outbound Q2"
        createMore={createMore}
        onCreateMoreChange={setCreateMore}
        submitLabel="Create lead"
        onSubmit={doSubmit}
        canSubmit={displayName.trim().length > 0}
        pending={busy}
        error={error}
        pills={
          <>
            <ComposerSelectPill
              icon={<CircleDashed />}
              placeholder="Status"
              value={status}
              onValueChange={setStatus}
              options={statuses.map((s) => ({ value: s.slug, label: s.name }))}
              ariaLabel="Status"
            />
            <ComposerSelectPill
              icon={<Route />}
              placeholder="Source"
              value={source}
              onValueChange={setSource}
              options={[
                { value: "__none__", label: "No source" },
                ...sources.map((s) => ({ value: s.slug, label: s.name })),
              ]}
              ariaLabel="Source"
            />
          </>
        }
      />
    );
  }

  return (
    <FormShell
      title="New lead"
      description="Capture the basics. You can add contacts later."
      onSubmit={(e) => {
        e.preventDefault();
        void doSubmit();
      }}
      error={error}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel ?? (() => navigate.back())}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !displayName}>
            {busy ? "Saving…" : "Create lead"}
          </Button>
        </>
      }
    >
      <FormField label="Lead name" required>
        <Input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Acme Co — outbound Q2"
          autoFocus
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Status">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.slug} value={s.slug}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Source">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger>
              <SelectValue placeholder="— None —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {sources.map((s) => (
                <SelectItem key={s.slug} value={s.slug}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
    </FormShell>
  );
}

// ------------------------------------------------------------ New opportunity

export function NewOpportunityClient({
  basePath,
  presetLeadId,
  leads,
  pipelines,
  owners,
  embedded = false,
  open,
  onOpenChange,
  onCreated,
  onCancel,
}: {
  basePath: string;
  presetLeadId: number | null;
  leads: { id: number; display_name: string }[];
  pipelines: {
    id: number;
    name: string;
    is_default: number;
    stages: { id: number; name: string }[];
  }[];
  owners: { id: number; label: string }[];
  /** Render as the shared Linear-style modal (`CreateComposer`) instead of the
   *  standalone /new page form. `open`/`onOpenChange` drive the dialog. */
  embedded?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Called after a successful create instead of navigating to the new deal. */
  onCreated?: () => void;
  /** Cancel handler; defaults to `navigate.back()` on the dedicated /new page. */
  onCancel?: () => void;
}) {
  const navigate = useNavigate();
  const defaultPipeline =
    pipelines.find((p) => p.is_default === 1) ?? pipelines[0];

  const [leadId, setLeadId] = useState<string>(
    presetLeadId != null ? String(presetLeadId) : "",
  );
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [pipelineId, setPipelineId] = useState<string>(
    defaultPipeline ? String(defaultPipeline.id) : "",
  );
  const [stageId, setStageId] = useState<string>("");
  const [expectedClose, setExpectedClose] = useState("");
  const [owner, setOwner] = useState<string>("__none__");
  const [createMore, setCreateMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stages = useMemo(
    () => pipelines.find((p) => String(p.id) === pipelineId)?.stages ?? [],
    [pipelines, pipelineId],
  );

  function onPipelineChange(next: string) {
    setPipelineId(next);
    setStageId(""); // reset stage when the pipeline changes
  }

  async function doSubmit() {
    if (!leadId || !name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const dollars = value.trim() === "" ? null : Number(value);
      const opp = await apiCall(oppsContract.create, {
        body: {
          lead_id: Number(leadId),
          name: name.trim(),
          value_minor:
            dollars != null && !Number.isNaN(dollars)
              ? Math.round(dollars * 100)
              : undefined,
          pipeline_id: pipelineId ? Number(pipelineId) : undefined,
          stage_id: stageId ? Number(stageId) : undefined,
          expected_close_date: expectedClose || null,
          owner_actor_id: owner === "__none__" ? null : Number(owner),
        },
      });
      if (onCreated) {
        if (createMore) {
          setName("");
          setValue("");
          setExpectedClose("");
          setBusy(false);
          navigate.refresh();
          return;
        }
        onCreated();
      } else {
        navigate.push(`${basePath}/opportunities/${opp.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  if (embedded) {
    return (
      <CreateComposer
        open={open ?? false}
        onOpenChange={(o) => onOpenChange?.(o)}
        contextLabel="Deal"
        contextIcon={<CircleDollarSign />}
        heading="New opportunity"
        title={name}
        onTitleChange={setName}
        titlePlaceholder="Acme — annual plan"
        createMore={createMore}
        onCreateMoreChange={setCreateMore}
        submitLabel="Create opportunity"
        onSubmit={doSubmit}
        canSubmit={Boolean(leadId) && name.trim().length > 0}
        pending={busy}
        error={error}
        pills={
          <>
            <ComposerSelectPill
              icon={<Users />}
              placeholder="Lead"
              value={leadId}
              onValueChange={setLeadId}
              options={leads.map((l) => ({
                value: String(l.id),
                label: l.display_name,
              }))}
              ariaLabel="Lead"
            />
            <ComposerSelectPill
              icon={<GitBranch />}
              placeholder="Pipeline"
              value={pipelineId}
              onValueChange={onPipelineChange}
              options={pipelines.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              ariaLabel="Pipeline"
            />
            <ComposerSelectPill
              icon={<Milestone />}
              placeholder="Stage"
              value={stageId}
              onValueChange={setStageId}
              options={stages.map((s) => ({ value: String(s.id), label: s.name }))}
              ariaLabel="Stage"
            />
            <ComposerSelectPill
              icon={<UserRound />}
              placeholder="Owner"
              value={owner}
              onValueChange={setOwner}
              options={[
                { value: "__none__", label: "Unassigned" },
                ...owners.map((o) => ({ value: String(o.id), label: o.label })),
              ]}
              ariaLabel="Owner"
            />
          </>
        }
      >
        {leads.length === 0 ? (
          <p className="type-body text-muted-foreground">
            No leads yet — create a lead first, then attach a deal to it.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Value (USD)" htmlFor="opp-value">
              <Input
                id="opp-value"
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0.00"
              />
            </FormField>
            <FormField label="Expected close" htmlFor="opp-close">
              <Input
                id="opp-close"
                type="date"
                value={expectedClose}
                onChange={(e) => setExpectedClose(e.target.value)}
              />
            </FormField>
          </div>
        )}
      </CreateComposer>
    );
  }

  return (
    <FormShell
      title="New opportunity"
      description="A deal always belongs to a lead. Pick the lead, name the deal, set its value."
      onSubmit={(e) => {
        e.preventDefault();
        void doSubmit();
      }}
      error={error}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel ?? (() => navigate.back())}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !leadId || !name.trim()}>
            {busy ? "Saving…" : "Create opportunity"}
          </Button>
        </>
      }
    >
      <FormField label="Lead" required>
        {leads.length === 0 ? (
          <p className="type-body text-muted-foreground">
            No leads yet — create a lead first, then attach a deal to it.
          </p>
        ) : (
          <Select value={leadId} onValueChange={setLeadId}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a lead" />
            </SelectTrigger>
            <SelectContent>
              {leads.map((l) => (
                <SelectItem key={l.id} value={String(l.id)}>
                  {l.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FormField>
      <FormField label="Deal name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme — annual plan"
          autoFocus
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Value (USD)">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="0.00"
          />
        </FormField>
        <FormField label="Expected close">
          <Input
            type="date"
            value={expectedClose}
            onChange={(e) => setExpectedClose(e.target.value)}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Pipeline">
          <Select value={pipelineId} onValueChange={onPipelineChange}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a pipeline" />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Stage">
          <Select
            value={stageId}
            onValueChange={setStageId}
            disabled={stages.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="First stage" />
            </SelectTrigger>
            <SelectContent>
              {stages.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <FormField label="Owner">
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger>
            <SelectValue placeholder="— Unassigned —" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Unassigned —</SelectItem>
            {owners.map((o) => (
              <SelectItem key={o.id} value={String(o.id)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    </FormShell>
  );
}

// ------------------------------------------------------------ New sequence

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function NewSequenceClient({
  basePath,
  embedded = false,
  onCreated,
  onCancel,
}: {
  basePath: string;
  /** Render for a dialog: drop the Card chrome (the dialog owns the header). */
  embedded?: boolean;
  /** Called after a successful create instead of navigating to the sequence. */
  onCreated?: () => void;
  /** Cancel handler; defaults to `navigate.back()` on the dedicated /new page. */
  onCancel?: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !effectiveSlug) return;
    setBusy(true);
    setError(null);
    try {
      const seq = await apiCall(sequencesContract.create, {
        body: {
          slug: effectiveSlug,
          name: name.trim(),
          description: description.trim() || null,
        },
      });
      if (onCreated) onCreated();
      else navigate.push(`${basePath}/sequences/${seq.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <FormShell
      title={embedded ? undefined : "New sequence"}
      description={
        embedded
          ? undefined
          : "Name the outbound cadence. You'll add day-offset steps next."
      }
      compact={embedded}
      onSubmit={submit}
      error={error}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel ?? (() => navigate.back())}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !name.trim() || !effectiveSlug}>
            {busy ? "Saving…" : "Create sequence"}
          </Button>
        </>
      }
    >
      <FormField label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Outbound — Q2 enterprise"
          autoFocus
        />
      </FormField>
      <FormField label="Slug" required>
        <Input
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(slugify(e.target.value));
          }}
          placeholder="outbound-q2-enterprise"
        />
      </FormField>
      <FormField label="Description">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this cadence is for and who it targets."
          rows={3}
        />
      </FormField>
    </FormShell>
  );
}

// ------------------------------------------------------------ New template (dedicated page)

export function NewTemplateClient({ basePath }: { basePath: string }) {
  const navigate = useNavigate();
  return (
    <CrmTemplateEditor
      onSubmit={async (input) => {
        await apiCall(templatesContract.create, { body: input });
        navigate.push(`${basePath}/templates`);
      }}
    />
  );
}

// ------------------------------------------------------------ Lead activity composer

export function LeadActivityClient({ leadId }: { leadId: number }) {
  const navigate = useNavigate();

  async function onSubmit(
    kind: "note" | "call" | "email" | "sms" | "meeting",
    payload: Record<string, unknown>,
  ) {
    if (kind === "call") {
      await apiCall(callsContract.log, { body: payload as never });
    } else if (kind === "email") {
      await apiCall(emailsContract.log, { body: payload as never });
    } else if (kind === "sms") {
      await apiCall(smsContract.log, { body: payload as never });
    } else if (kind === "meeting") {
      await apiCall(meetingsContract.create, { body: payload as never });
    } else if (kind === "note") {
      await apiCall(emailsContract.log, {
        body: {
          lead_id: leadId,
          direction: "outbound_logged",
          subject: "Note",
          body_text: String(payload.body ?? ""),
        } as never,
      });
    }
    navigate.refresh();
  }

  return <ActivityComposer leadId={leadId} onSubmit={onSubmit} />;
}

// ------------------------------------------------------------ Pipeline board

export function PipelineBoardClient({
  stages,
  opportunities,
}: {
  /** `basePath` is no longer read here — cards open the scope-free /o/<uid>. */
  basePath: string;
  stages: PipelineStage[];
  // The board card's `id` is the numeric opportunity id; `uid` is the synced
  // object id the rows now carry so the card opens at /o/<uid> (no 404 at the
  // workspace tier, where `${basePath}/opportunities/<id>` has no route).
  opportunities: (OpportunityCardData & { stage_id: number; uid: string })[];
}) {
  const navigate = useNavigate();
  const openObject = useOpenObject();
  const uidById = new Map(opportunities.map((o) => [o.id, o.uid]));
  return (
    <CrmPipelineBoard
      stages={stages}
      opportunities={opportunities}
      onMove={async (oppId, stageId) => {
        await apiCall(oppsContract.move_stage, {
          params: { id: oppId },
          body: { stage_id: stageId },
        });
        navigate.refresh();
      }}
      onOpenOpportunity={(id) => {
        const uid = uidById.get(id);
        if (uid) openObject(uid);
      }}
    />
  );
}

// ------------------------------------------------------------ Opportunity stage actions

export function OpportunityMoveClient({
  opportunity,
  stages,
}: {
  opportunity: { id: number; stage_id: number; value_minor: number; currency: string };
  stages: { id: number; name: string }[];
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function moveTo(id: number) {
    if (id === opportunity.stage_id) return;
    setBusy(true);
    try {
      await apiCall(oppsContract.move_stage, {
        params: { id: opportunity.id },
        body: { stage_id: id },
      });
      navigate.refresh();
    } finally {
      setBusy(false);
    }
  }

  async function win() {
    setBusy(true);
    try {
      await apiCall(oppsContract.win, { params: { id: opportunity.id } });
      navigate.refresh();
    } finally {
      setBusy(false);
    }
  }

  async function lose() {
    const reason = typeof window !== "undefined" ? window.prompt("Lost reason?") : null;
    setBusy(true);
    try {
      await apiCall(oppsContract.lose, {
        params: { id: opportunity.id },
        body: { lost_reason: reason },
      });
      navigate.refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="type-body text-muted-foreground">Move to:</span>
      {stages.map((s) => (
        <Button
          key={s.id}
          size="sm"
          variant={s.id === opportunity.stage_id ? "default" : "outline"}
          onClick={() => moveTo(s.id)}
          disabled={busy}
        >
          {s.name}
        </Button>
      ))}
      <div className="ml-auto flex gap-2">
        <Button size="sm" variant="outline" onClick={win} disabled={busy}>
          Mark won
        </Button>
        <Button size="sm" variant="outline" onClick={lose} disabled={busy}>
          Mark lost
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Sequence editor

export function SequenceEditorClient({
  sequence,
  steps,
  templates,
}: {
  sequence: { id: number; name: string; description: string | null; paused: number };
  steps: SequenceStep[];
  templates: { id: number; name: string }[];
}) {
  const navigate = useNavigate();
  return (
    <SequenceEditor
      sequence={sequence}
      steps={steps}
      templates={templates}
      onAddStep={async (step) => {
        await apiCall(sequencesContract.step_create, {
          params: { id: sequence.id },
          body: step,
        });
        navigate.refresh();
      }}
    />
  );
}

// ------------------------------------------------------------ Template editor

export function TemplatesClient() {
  const navigate = useNavigate();
  return (
    <CrmTemplateEditor
      onSubmit={async (input) => {
        await apiCall(templatesContract.create, { body: input });
        navigate.refresh();
      }}
    />
  );
}

// CSV import moved to the shared typed-import pattern — the sales Import tab now
// renders `DataImportClient` (excellent/src/data-import/client.tsx) against the
// `crm-leads` target, retiring the CRM-only wizard + `/api/crm/import` fork.
